import { Stack, Typography } from '@mui/material'
import { OrderResponseDto } from '@planning/app/api'
import { OrderItemJourneyStatusChipWrapper } from '@planning/components/molecules/ContainerJourneyStatusChipFFWrapper'
import { columnFilterPresets } from '@planning/pages/VesselVisit/tableConfigs/columnFilterConfigs'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  getMultiSelectRenderBadge,
  useContainerAttributesParameters,
} from '@tom-ui/ui'
import { MRT_ColumnDef, MRT_Row } from 'material-react-table'
import { ReactNode } from 'react'
import { releaseFilterPresets } from './releaseFilterConfigs'

export const getCarrierIdentifier = (order: IOrderItem) => {
  if (!order.visit) return EmptyIndicator

  if ('vessels' in order.visit && order.visit.vessels.length)
    return order.visit.vessels[0].data.name
  if ('truck' in order.visit && order.visit.truck) return order.visit.truck.data.licensePlate
  if ('railcarEstimate' in order.visit.data && 'name' in order.visit.data && order.visit.data.name)
    return order.visit.data.name

  return EmptyIndicator
}

export const useNonNumericReleaseDetailsColumns = () => {
  const { t } = useTranslate()
  const { heights, types } = useContainerAttributesParameters()

  const getContainerNumber = (order: OrderResponseDto, renderedCellValue: ReactNode) => {
    let containerNumber: ReactNode | string = EmptyIndicator
    if (order.containerNumber) containerNumber = renderedCellValue

    if (order.suggestedEmptyContainerNumber) {
      return (
        <Stack>
          <Typography variant='subtitle2'>{t('suggested', 'Suggested')}</Typography>
          {renderedCellValue}
        </Stack>
      )
    }

    return containerNumber
  }

  const columns: MRT_ColumnDef<IOrderItem>[] = [
    {
      ...releaseFilterPresets.searchMandatory(),
      accessorKey: 'data.containerNumber',
      header: t('Container no', 'Container no'),
      size: 200,
      accessorFn: row => row.data?.containerNumber ?? row.data?.suggestedEmptyContainerNumber ?? '',
      Cell: ({ row, renderedCellValue }) =>
        getContainerNumber(row.original.data, renderedCellValue),
    },
    {
      ...columnFilterPresets.orderStatusMultiSelect(),
      accessorKey: 'containerJourney.status',
      header: t('Status', 'Status'),
      size: 150,
      accessorFn: row => row.containerJourney?.status ?? '-',
      Cell: ({ row }: { row: MRT_Row<IOrderItem> }) => (
        <OrderItemJourneyStatusChipWrapper orderItem={row.original} />
      ),
      muiFilterTextFieldProps: ({ column }) =>
        getMultiSelectRenderBadge(column, t, t('filterByStatus', 'Filter by Status')),
    },
    {
      ...releaseFilterPresets.searchMandatory(),
      accessorKey: 'data.containerIsoCode',
      header: t('ISO Code', 'ISO Code'),
      Cell: ({ row }) =>
        row.original.data.containerIsoCode ? row.original.data.containerIsoCode : EmptyIndicator,
    },
    {
      ...releaseFilterPresets.numericMandatory(),
      accessorKey: 'data.containerLength',
      header: t('length', 'Length'),
      Cell: ({ row }) =>
        row.original.data.containerLength
          ? `${row.original.data.containerLength}ft`
          : EmptyIndicator,
      size: 100,
    },
    {
      ...releaseFilterPresets.heightMultiSelect(t),
      accessorKey: 'data.containerHeight',
      header: t('height', 'Height'),
      accessorFn: row =>
        row.data?.containerHeight ? heights[row.data.containerHeight]?.label : EmptyIndicator,
      muiFilterTextFieldProps: ({ column }) =>
        getMultiSelectRenderBadge(column, t, t('filterByHeight', 'Filter by Height')),
      size: 100,
    },
    {
      ...releaseFilterPresets.typeMultiSelect(t),
      accessorKey: 'data.containerType',
      header: t('Type', 'Type'),
      accessorFn: row =>
        row.data?.containerType ? types[row.data.containerType]?.label : EmptyIndicator,
    },
    {
      ...releaseFilterPresets.searchMandatory(),
      accessorFn: row => getCarrierIdentifier(row),
      header: t('Carrier', 'Carrier'),
    },
  ]

  return columns
}
